/**
 * CategoryContext — Provides dynamic category taxonomy and bucket names app-wide.
 *
 * - categoryDefs: rows from user_category_defs table (empty array = use CATEGORY_TAXONOMY defaults)
 * - bucketNames: {need, want, savings, income} display labels (user-customizable)
 * - effectiveTaxonomy: [{group, bucket, categories}] ready for CategoryPickerModal
 * - usingDefaults: true when user has no custom defs (shows CATEGORY_TAXONOMY)
 * - refresh(): reload from DB (call after any add/edit/delete)
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getUserCategoryDefs, getUserBucketNames } from '../services/db';
import { CATEGORY_TAXONOMY } from '../utils/categories';

export const DEFAULT_BUCKET_NAMES = {
  need: 'Needs',
  want: 'Wants',
  savings: 'Savings',
  income: 'Income',
};

/**
 * Converts flat user_category_defs rows into CATEGORY_TAXONOMY-compatible format:
 *   [{group: bucketDisplayName, bucket: 'need'|..., categories: [{label, bucket}]}]
 *
 * Falls back to CATEGORY_TAXONOMY when userDefs is empty.
 */
export function buildEffectiveTaxonomy(userDefs, bucketNames) {
  if (!userDefs || userDefs.length === 0) return CATEGORY_TAXONOMY;

  const names = { ...DEFAULT_BUCKET_NAMES, ...bucketNames };
  const bucketOrder = ['income', 'need', 'want', 'savings'];
  const grouped = {};

  for (const def of [...userDefs].sort((a, b) => a.sort_order - b.sort_order)) {
    if (!grouped[def.bucket]) grouped[def.bucket] = [];
    grouped[def.bucket].push({ label: def.label, bucket: def.bucket });
  }

  return bucketOrder
    .filter(b => grouped[b]?.length)
    .map(b => ({ group: names[b], bucket: b, categories: grouped[b] }));
}

const CategoryContext = createContext(null);

export function CategoryProvider({ children }) {
  const { user } = useAuth();
  const [categoryDefs, setCategoryDefs] = useState([]);
  const [bucketNames, setBucketNames] = useState(DEFAULT_BUCKET_NAMES);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const [defs, names] = await Promise.all([
        getUserCategoryDefs(user.id),
        getUserBucketNames(user.id),
      ]);
      setCategoryDefs(defs);
      setBucketNames(names);
    } catch (err) {
      console.error('CategoryContext load failed:', err);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const effectiveTaxonomy = buildEffectiveTaxonomy(categoryDefs, bucketNames);

  return (
    <CategoryContext.Provider value={{
      categoryDefs,
      bucketNames,
      effectiveTaxonomy,
      usingDefaults: categoryDefs.length === 0,
      refresh,
      setCategoryDefs,
      setBucketNames,
    }}>
      {children}
    </CategoryContext.Provider>
  );
}

export function useCategoryContext() {
  const ctx = useContext(CategoryContext);
  if (!ctx) throw new Error('useCategoryContext must be used within CategoryProvider');
  return ctx;
}
